import {
  Component,
  input,
  output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  signal,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../shared.module';

/**
 * "Impact Area + Focus Areas" info box (Figma 379-8100 / 492-5098 / 492-5451).
 *
 * Renders an Impact Area group (Country / Region rows, or "Global") and a
 * Focus Areas group (category pills), each collapsing to a single line with a
 * "+N more" control. Extracted from the catalog card so it can be reused (e.g.
 * on the project details Organization card).
 *
 * The "+N more" control emits `moreClicked`; the consumer decides what it does
 * (the catalog card navigates to the entity details page).
 */
@Component({
  selector: 'app-catalog-card-impact',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './catalog-card-impact.component.html',
  styleUrls: ['./catalog-card-impact.component.scss'],
})
export class CatalogCardImpactComponent implements AfterViewInit, OnDestroy {
  countries = input<string[]>([]);
  regions = input<string[]>([]);
  focusAreas = input<string[]>([]);
  globalInfluence = input<boolean>(false);

  /**
   * When true, the box reserves a fixed minimum height and, when empty, renders
   * an invisible spacer instead of collapsing — used by the catalog card to keep
   * a consistent card height. When false, an empty box takes no space.
   */
  reserveHeight = input<boolean>(false);

  /** Emitted when a "+N more" control is activated; the consumer decides navigation. */
  moreClicked = output<Event>();

  @ViewChild('countriesContainer', { read: ElementRef })
  countriesContainer?: ElementRef<HTMLElement>;

  @ViewChild('regionsContainer', { read: ElementRef })
  regionsContainer?: ElementRef<HTMLElement>;

  @ViewChild('focusAreasContainer', { read: ElementRef })
  focusAreasContainer?: ElementRef<HTMLElement>;

  // Conservative defaults avoid an overflow flash before the post-render measurement runs.
  maxVisibleCountries = signal(4);
  maxVisibleRegions = signal(4);
  maxVisibleFocusAreas = signal(3);
  countriesExpanded = false;
  regionsExpanded = false;
  focusAreasExpanded = false;

  private countriesResizeObserver?: ResizeObserver;
  private regionsResizeObserver?: ResizeObserver;
  private focusAreasResizeObserver?: ResizeObserver;

  constructor() {
    // Recalculate one-line fit whenever the data inputs change.
    effect(() => {
      this.countries();
      this.regions();
      this.focusAreas();
      this.countriesExpanded = false;
      this.regionsExpanded = false;
      this.focusAreasExpanded = false;
      setTimeout(() => {
        this.setupObservers();
        this.calculateVisibleCountries();
        this.calculateVisibleRegions();
        this.calculateVisibleFocusAreas();
      }, 0);
    });
  }

  ngAfterViewInit(): void {
    this.setupObservers();
    this.calculateVisibleCountries();
    this.calculateVisibleRegions();
    this.calculateVisibleFocusAreas();
  }

  ngOnDestroy(): void {
    this.countriesResizeObserver?.disconnect();
    this.regionsResizeObserver?.disconnect();
    this.focusAreasResizeObserver?.disconnect();
  }

  onMore(event: Event): void {
    // Prevent the click from bubbling to a clickable parent card before the
    // consumer handles it (e.g. navigating to the entity details page).
    event.preventDefault();
    event.stopPropagation();
    this.moreClicked.emit(event);
  }

  /** Whether the entity influences all countries — shows "Global" instead of a list. */
  get isGlobalInfluence(): boolean {
    return !!this.globalInfluence();
  }

  /** Whether the box has any Impact Area or Focus Area data to show. */
  get hasContent(): boolean {
    return this.isGlobalInfluence
      || this.countries().length > 0
      || this.regions().length > 0
      || this.focusAreas().length > 0;
  }

  get visibleCountries(): string[] {
    const countries = this.countries();
    return this.countriesExpanded ? countries : countries.slice(0, this.maxVisibleCountries());
  }

  get countriesHasOverflow(): boolean {
    return this.countries().length > this.maxVisibleCountries();
  }

  get countriesOverflowCount(): number {
    return Math.max(0, this.countries().length - this.maxVisibleCountries());
  }

  get visibleRegions(): string[] {
    const regions = this.regions();
    return this.regionsExpanded ? regions : regions.slice(0, this.maxVisibleRegions());
  }

  get regionsHasOverflow(): boolean {
    return this.regions().length > this.maxVisibleRegions();
  }

  get regionsOverflowCount(): number {
    return Math.max(0, this.regions().length - this.maxVisibleRegions());
  }

  get visibleFocusAreas(): string[] {
    const areas = this.focusAreas();
    return this.focusAreasExpanded ? areas : areas.slice(0, this.maxVisibleFocusAreas());
  }

  get focusHasOverflow(): boolean {
    return this.focusAreas().length > this.maxVisibleFocusAreas();
  }

  get focusOverflowCount(): number {
    return Math.max(0, this.focusAreas().length - this.maxVisibleFocusAreas());
  }

  /**
   * Lazily attach ResizeObservers to the row/pill containers. They only exist
   * once the box has rendered, so this runs from ngAfterViewInit and the effect.
   */
  private setupObservers(): void {
    if (this.countriesContainer && !this.countriesResizeObserver) {
      this.countriesResizeObserver = new ResizeObserver(() => this.calculateVisibleCountries());
      this.countriesResizeObserver.observe(this.countriesContainer.nativeElement);
    }
    if (this.regionsContainer && !this.regionsResizeObserver) {
      this.regionsResizeObserver = new ResizeObserver(() => this.calculateVisibleRegions());
      this.regionsResizeObserver.observe(this.regionsContainer.nativeElement);
    }
    if (this.focusAreasContainer && !this.focusAreasResizeObserver) {
      this.focusAreasResizeObserver = new ResizeObserver(() => this.calculateVisibleFocusAreas());
      this.focusAreasResizeObserver.observe(this.focusAreasContainer.nativeElement);
    }
  }

  /**
   * Compute how many comma-separated values fit on a single line, reserving room
   * for the "+N more" suffix when the list is truncated.
   */
  private commaListFitCount(container: HTMLElement | undefined, items: string[]): number | null {
    if (!container || items.length === 0) {
      return null;
    }

    const available = container.offsetWidth;
    if (available === 0) {
      return null;
    }

    const probe = document.createElement('span');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.whiteSpace = 'nowrap';
    probe.style.fontFamily = 'Rethink Sans, sans-serif';
    probe.style.fontSize = '12px';
    document.body.appendChild(probe);

    const measure = (text: string, weight: string): number => {
      probe.style.fontWeight = weight;
      probe.textContent = text;
      return probe.offsetWidth;
    };

    const gap = 10; // gap between value text and the "+N more" suffix
    let count = 1;
    for (let k = items.length; k >= 1; k--) {
      let needed = measure(items.slice(0, k).join(', '), '500');
      if (k < items.length) {
        needed += gap + measure(`+${items.length - k} more`, '600');
      }
      if (needed <= available || k === 1) {
        count = k;
        break;
      }
    }

    document.body.removeChild(probe);
    return count;
  }

  private calculateVisibleCountries(): void {
    const count = this.commaListFitCount(this.countriesContainer?.nativeElement, this.countries());
    if (count !== null) {
      this.maxVisibleCountries.set(count);
    }
  }

  private calculateVisibleRegions(): void {
    const count = this.commaListFitCount(this.regionsContainer?.nativeElement, this.regions());
    if (count !== null) {
      this.maxVisibleRegions.set(count);
    }
  }

  /**
   * Compute how many Focus Area pills fit on a single line, reserving room for
   * the "+N more" suffix when truncated.
   */
  private calculateVisibleFocusAreas(): void {
    const container = this.focusAreasContainer?.nativeElement;
    const areas = this.focusAreas();
    if (!container || areas.length === 0) {
      return;
    }

    const available = container.offsetWidth;
    if (available === 0) {
      return;
    }

    const probe = document.createElement('span');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.whiteSpace = 'nowrap';
    probe.style.boxSizing = 'border-box';
    probe.style.fontFamily = 'Rethink Sans, sans-serif';
    probe.style.fontSize = '12px';
    document.body.appendChild(probe);

    const pillWidth = (text: string): number => {
      probe.style.fontWeight = '400';
      probe.style.padding = '3px 10px';
      probe.textContent = text;
      return probe.offsetWidth;
    };
    const moreWidth = (text: string): number => {
      probe.style.fontWeight = '600';
      probe.style.padding = '0';
      probe.textContent = text;
      return probe.offsetWidth;
    };

    const gap = 9;
    let count = 1;
    for (let k = areas.length; k >= 1; k--) {
      let needed = 0;
      for (let i = 0; i < k; i++) {
        needed += pillWidth(areas[i]);
      }
      needed += (k - 1) * gap;
      if (k < areas.length) {
        needed += gap + moreWidth(`+${areas.length - k} more`);
      }
      if (needed <= available || k === 1) {
        count = k;
        break;
      }
    }

    document.body.removeChild(probe);
    this.maxVisibleFocusAreas.set(count);
  }
}
